/** RULĂRILE AGENTULUI PLANULUI, ÎN CONVERSAȚIE (partea pură a cardurilor).
 *
 *  Omul cere ceva în discuție, asistentul predă cererea agentului, agentul
 *  lucrează minute întregi. Răspunsul la cerere e RULAREA, deci cardul ei
 *  (starea, firul, cifrele de la sfârșit) stă SUB cererea care a pornit-o, ca
 *  un răspuns, nu deasupra chatului (Oana, 30.10.2026). O rulare nouă primește
 *  un card nou sub cererea ei; cardurile vechi rămân înghețate, cu firul și
 *  cifrele lor. Istoricul e doar al ecranului (`sessionStorage` pe planul
 *  deschis): serverul ține numai ULTIMA rulare (`GET …/agent`), iar discuția
 *  asistentului nu știe de joburi.
 *
 *  LEGAREA DE CERERE se face pe ORDINALUL mesajului omului în discuție
 *  (`dupaOm`: 0 = primul), nu pe un id: discuția se reia de pe server la
 *  deschidere cu aceleași mesaje în aceeași ordine, deci ordinalul rămâne bun
 *  și după o reîncărcare. `null` = înaintea tuturor: o rulare găsită rulând la
 *  deschiderea paginii, fără cerere în istoric. Butonul «Refă planul» n-are
 *  mesaj al lui: cardul lui stă sub ultimul mesaj de atunci.
 *
 *  Firul viu nu se ține aici (vine din `useFirAgent`); aici intră abia când
 *  rularea se încheie, înghețat. Funcțiile întorc ACELAȘI tablou când n-au
 *  schimbat nimic: ele rulează la fiecare rând nou al firului, iar un tablou
 *  nou ar fi redesenat discuția și rescris stocarea degeaba.
 *
 *  AL CUI E FIRUL: endpoint-ul dă firul ULTIMULUI job, fără să spună care, iar
 *  după `startJob` serverul mai întoarce o vreme firul jobului vechi (sau îl
 *  dă pe cel nou gol, `pending`). O rulare abia pornită ține `la` al ultimului
 *  rând de dinaintea ei: rânduri care se termină cu alt `la` sunt ale ei
 *  (`firulEi`). Până atunci cardul rămâne «a pornit», orice ar zice `ruleaza`
 *  — panoul forțează sondajul firului cât așteaptă, deci `ruleaza` nu e o
 *  dovadă că jobul nou a fost văzut. */
import type { RandFir, StareFir } from "../fatada/firAgent";
import type { AgentPlanRezumat } from "./firPlan";

export type SursaRularii = "cerere" | "buton" | "gasita";

export interface Rulare {
  /** id-ul ecranului, nu al jobului: firul nu spune jobul */
  id: string;
  dupaOm: number | null;
  sursa: SursaRularii;
  /** `pornita` = jobul e scris, registrul nu-l vede încă rulând; `ruleaza` =
   *  firul e viu (cardul citește firul, nu `randuri`); `incheiata` = înghețată */
  stare: "pornita" | "ruleaza" | "incheiata";
  /** firul înghețat la încheiere */
  randuri: RandFir[];
  /** cifrele rulării, din `summary.agent_plan`, după ce serverul le-a scris */
  rezumat: AgentPlanRezumat | null;
  /** `la` al rezumatului de DINAINTEA rulării: un rezumat cu alt `la` e al ei.
   *  Fără ceasuri comparate între server și browser. */
  laDinainte: string | null;
  /** `la` al ultimului rând de fir de DINAINTEA pornirii (firul jobului
   *  vechi); `null` = nu era niciun fir, deci orice rânduri sunt ale ei. */
  firDinainte: string | null;
  pornitaLa: string;
}

const ultimulLa = (randuri: readonly RandFir[]): string | null =>
  randuri.length ? randuri[randuri.length - 1].la ?? null : null;

/** Firul citit e al rulării `r`, nu al jobului de dinaintea ei? Fără rânduri
 *  nu e al nimănui: un job abia scris (`pending`) n-a spus încă nimic. */
export const firulEi = (r: Rulare, fir: StareFir): boolean =>
  fir.randuri.length > 0 && (r.firDinainte === null || ultimulLa(fir.randuri) !== r.firDinainte);

/** Cardul arată «a pornit; aștept primii pași» — nu doar pe `pornita`, ci și
 *  pe o rulare vie al cărei fir e încă gol (jobul văzut `pending`, sau abia
 *  `running`, fără niciun rând): o cronologie goală n-ar spune nimic. */
export const asteaptaPrimiiPasi = (r: Rulare, fir: StareFir | null): boolean =>
  r.stare === "pornita" || (r.stare === "ruleaza" && !!fir && fir.randuri.length === 0);

/** Rularea care nu s-a încheiat (cel mult una: serverul refuză a doua). */
export const rulareaVie = (rulari: Rulare[]): Rulare | undefined =>
  rulari.find((r) => r.stare !== "incheiata");

/** O rulare pornită de pe ecran (unealta sau butonul), sub al `dupaOm`-lea
 *  mesaj al omului; `firDinainte` = firul de pe ecran în clipa pornirii (al
 *  jobului vechi). O rulare vie rămasă (n-ar trebui: serverul a dat 409)
 *  se închide, ca să nu rămână două carduri care citesc același fir. */
export function cuRularePornita(rulari: Rulare[], sursa: "cerere" | "buton",
                                dupaOm: number | null, rezumat: AgentPlanRezumat | null,
                                firDinainte: readonly RandFir[],
                                acum = new Date().toISOString()): Rulare[] {
  const inchise = rulari.map((r) => r.stare === "incheiata" ? r : { ...r, stare: "incheiata" as const });
  return [...inchise, {
    id: `${acum}-${inchise.length}`, dupaOm, sursa, stare: "pornita", randuri: [],
    rezumat: null, laDinainte: rezumat?.la ?? null, firDinainte: ultimulLa(firDinainte), pornitaLa: acum,
  }];
}

/** Istoricul după o citire a firului. Fără nimic viu, un fir care rulează e
 *  o rulare GĂSITĂ (pornită înainte de deschiderea paginii, sau din altă
 *  fereastră): card nou, înaintea conversației. Una abia `pornita` așteaptă
 *  primele rânduri ALE EI (`firulEi`): cu ele, trece pe `ruleaza` dacă firul
 *  e viu, sau direct pe `incheiata` dacă jobul a și terminat (o rulare scurtă,
 *  sau pagina reîncărcată după sfârșit). Firul vechi sau gol n-o schimbă —
 *  nici dacă `ruleaza` e forțat de panou (v. `faraPornirileUitate`). Una care
 *  `ruleaza` se îngheață cu rândurile firului când el se oprește. */
export function laFir(rulari: Rulare[], fir: StareFir, rezumat: AgentPlanRezumat | null,
                      acum = new Date().toISOString()): Rulare[] {
  const vie = rulareaVie(rulari);
  if (!vie) {
    if (!fir.ruleaza) return rulari;
    return [{ id: `${acum}-gasita`, dupaOm: null, sursa: "gasita", stare: "ruleaza", randuri: [],
              rezumat: null, laDinainte: rezumat?.la ?? null, firDinainte: null, pornitaLa: acum }, ...rulari];
  }
  if (vie.stare === "pornita" && !firulEi(vie, fir)) return rulari;
  if (fir.ruleaza) {
    return vie.stare === "ruleaza" ? rulari
      : rulari.map((r) => r === vie ? { ...r, stare: "ruleaza" as const } : r);
  }
  return rulari.map((r) => r === vie
    ? { ...r, stare: "incheiata" as const, randuri: [...fir.randuri], rezumat: rezumatulEi(r, rezumat) } : r);
}

/** O pornire pe care nici registrul n-a văzut-o rulând după `limitaMs`:
 *  jobul n-a pornit (sau a și terminat fără fir); cardul ei pleacă. Cât
 *  registrul o vede (un `pending` lung la coadă), panoul nu cheamă asta. */
export function faraPornirileUitate(rulari: Rulare[], limitaMs: number,
                                    acum = Date.now()): Rulare[] {
  const ramase = rulari.filter((r) => !(r.stare === "pornita" && acum - Date.parse(r.pornitaLa) > limitaMs));
  return ramase.length === rulari.length ? rulari : ramase;
}

const rezumatulEi = (r: Rulare, rezumat: AgentPlanRezumat | null): AgentPlanRezumat | null =>
  rezumat && rezumat.la !== r.laDinainte ? rezumat : null;

/** Cifrele de la sfârșit vin din registru, de obicei DUPĂ ce firul s-a oprit:
 *  intră pe ultima rulare încheiată, cât nu rulează alta, dacă sunt ale ei
 *  (alt `la` decât înaintea ei). Un rezumat rescris între timp (starea
 *  finală după unul «în lucru») îl înlocuiește pe cel de dinainte. */
export function cuRezumatul(rulari: Rulare[], rezumat: AgentPlanRezumat | null): Rulare[] {
  if (!rezumat || rulareaVie(rulari)) return rulari;
  const ultima = rulari[rulari.length - 1];
  if (!ultima || ultima.stare !== "incheiata") return rulari;
  const al = rezumatulEi(ultima, rezumat);
  if (!al || ultima.rezumat?.la === al.la && ultima.rezumat?.status === al.status) return rulari;
  return rulari.map((r) => r === ultima ? { ...r, rezumat: al } : r);
}

/** Cardurile de pus în discuție, în forma pe care o știe `Asistent::carduri`:
 *  cheia și după al câtelea mesaj al omului stau. */
export const asezareaCardurilor = (rulari: Rulare[]) =>
  rulari.map((r) => ({ cheie: r.id, dupaOm: r.dupaOm }));

/** DISCUȚIA ÎMPLETITĂ CU CARDURILE: cardurile cu `dupaOm = null` înaintea
 *  tuturor; fiecare altul după al `dupaOm`-lea rând al omului (în ordinea
 *  istoricului). Un card al unui mesaj care nu e (încă) în discuție — istoric
 *  dintr-o discuție reluată altfel — stă la coadă, nu se pierde. */
export function impleteste<R extends { fel: string }, C extends { dupaOm: number | null }>(
  randuri: readonly R[], carduri: readonly C[],
): ({ fel: "rand"; rand: R; i: number } | { fel: "card"; card: C })[] {
  const out: ({ fel: "rand"; rand: R; i: number } | { fel: "card"; card: C })[] = [];
  const puse = new Set<C>();
  const pune = (c: C) => { out.push({ fel: "card", card: c }); puse.add(c); };
  carduri.filter((c) => c.dupaOm === null).forEach(pune);
  let om = -1;
  randuri.forEach((rand, i) => {
    out.push({ fel: "rand", rand, i });
    if (rand.fel !== "om") return;
    om += 1;
    carduri.filter((c) => c.dupaOm === om).forEach(pune);
  });
  carduri.filter((c) => !puse.has(c)).forEach(pune);
  return out;
}

const CHEIE = (planId: string) => `agent_plan.rulari.${planId}`;

/** Istoricul planului din `sessionStorage` (gol dacă lipsește sau e stricat).
 *  O rulare rămasă `ruleaza` la reîncărcare se pune pe `pornita`: firul o
 *  reia dacă încă rulează, o îngheață dacă s-a încheiat între timp (rândurile
 *  lui sunt ale ei, după `firDinainte`), altfel pleacă după limită. */
export function citesteIstoricul(planId: string): Rulare[] {
  try {
    const brut = JSON.parse(sessionStorage.getItem(CHEIE(planId)) ?? "[]");
    if (!Array.isArray(brut)) return [];
    return brut
      .filter((r): r is Rulare => !!r && typeof r === "object" && typeof r.id === "string")
      .map((r) => r.stare === "incheiata" ? r
        : { ...r, stare: "pornita" as const, pornitaLa: new Date().toISOString() });
  } catch {
    return [];
  }
}

export function scrieIstoricul(planId: string, rulari: Rulare[]): void {
  try {
    if (rulari.length) sessionStorage.setItem(CHEIE(planId), JSON.stringify(rulari));
    else sessionStorage.removeItem(CHEIE(planId));
  } catch {
    // fără stocare (fereastră privată, cotă plină): istoricul trăiește cât pagina
  }
}
